import { FC, ReactNode, CSSProperties } from 'react';
import { ICmsTheme } from '../services/types';
import { cn, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import Image from '@/src/components/Image';

type Props = {
  theme: ICmsTheme | null;
  children: ReactNode;
  header?: ReactNode;
  alt: string;
  id: string;
  imageSize?: PRODUCT_IMAGE_SIZE;
  className?: string;
};

const BG_OPTIONS = ['bg-linear-theme', 'bg-linear-orange'];

const getBackgroundClass = (theme: ICmsTheme | null, id: string | number): string => {
  if (!theme || (!theme.backgroundImage && !theme.backgroundColor)) {
    const index = String(id).charCodeAt(0) % BG_OPTIONS.length;
    return BG_OPTIONS[index];
  }
  if (theme.backgroundImage) return 'bg-linear-black';
  return '';
};

const getBackgroundStyle = (theme: ICmsTheme | null): CSSProperties => {
  if (theme?.backgroundColor && !theme.backgroundImage) {
    return {
      background: `linear-gradient(180deg, ${theme.backgroundColor} 0%, #FFFFFF 69.93%, #FFFFFF 99.91%)`,
    };
  }
  return {};
};

export const ThemeLayout: FC<Props> = ({
  children,
  header,
  theme,
  alt,
  id,
  className,
  imageSize,
}) => {
  const backgroundClass = getBackgroundClass(theme, id);
  const backgroundStyle = getBackgroundStyle(theme);

  return (
    <section className={cn('relative space-y-4 overflow-hidden rounded-t-3xl', className)}>
      <div
        className={cn('pointer-events-none absolute inset-0 z-4 h-full', backgroundClass)}
        style={backgroundStyle}
      />

      {theme?.backgroundImage && (
        <Image
          src={theme.backgroundImage}
          alt={alt}
          fill
          className="z-3 object-cover"
          size={imageSize}
        />
      )}

      <header
        className={cn(
          'relative z-5 px-3 pt-3 lg:px-6 lg:pt-6',
          theme?.backgroundImage && 'text-white',
        )}
      >
        {header}
      </header>
      <div className="relative z-5 pb-6">{children}</div>
    </section>
  );
};
